import { useEffect, useRef } from 'react';
import { IconArrowRightRegular, Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { PurchaseHistoryCard } from './PurchaseHistoryCard';
import { PurchaseHistoryCardItem } from './types';

interface PurchaseHistoryCardCarouselProps {
  items: PurchaseHistoryCardItem[];
  selectedId: string | null;
  onCardClick: (id: string) => void;
  onProductClick: (id: string) => void;
  onMoreClick: () => void;
  maxVisibleItems?: number;
  /** 선택 전 버튼 텍스트 (기본값: '비교하기') */
  actionButtonText?: string;
  /** 선택 후 버튼 텍스트 (기본값: '비교 중') */
  selectedButtonText?: string;
}

export const PurchaseHistoryCardCarousel = ({
  items,
  selectedId,
  onCardClick,
  onProductClick,
  onMoreClick,
  maxVisibleItems = 5,
  actionButtonText = '비교하기',
  selectedButtonText = '비교 중',
}: PurchaseHistoryCardCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 선택된 아이템이 변경될 때마다 스크롤을 맨 앞으로 이동시킵니다.
    if (scrollRef.current && selectedId) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [selectedId]);

  if (!items || items.length === 0) return null;

  const hasMore = items.length > maxVisibleItems;
  const visibleData = hasMore ? items.slice(0, maxVisibleItems) : items;

  const isSingleItem = items.length === 1;

  return (
    <PurchaseListWrapper ref={scrollRef}>
      {visibleData.map((item) => (
        <PurchaseHistoryCard
          key={item.uniqueKey}
          brandName={item.brandName}
          productName={item.productName}
          imageUrl={item.imageUrl}
          optionItemName={item.optionItemName ?? undefined}
          actionButtonText={item.uniqueKey === selectedId ? selectedButtonText : actionButtonText}
          actionButtonVariant={item.uniqueKey === selectedId ? 'tertiary' : 'primary'}
          isSelected={item.uniqueKey === selectedId}
          onCompareClick={() => onCardClick(item.uniqueKey)}
          onProductClick={() => onProductClick(item.uniqueKey)}
          isFilled={isSingleItem}
        />
      ))}
      {hasMore && (
        <MoreButton>
          <IconHolder onClick={onMoreClick}>
            <IconArrowRightRegular size={24} color='iconSecondary' />
          </IconHolder>
          <Text variant='Body_13_SemiBold' color='textSecondary'>
            더 보기
          </Text>
        </MoreButton>
      )}
    </PurchaseListWrapper>
  );
};

const PurchaseListWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-x;
  overscroll-behavior-x: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const IconHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid ${vars.color.borderPrimary};
  cursor: pointer;
`;

const MoreButton = styled.div`
  padding: 0 36px 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
`;
